import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'

export interface TraitPill {
  /**
   * Metric/trait name
   */
  name: string
  /**
   * Performance level
   */
  level: 'excellent' | 'good' | 'needsWork'
  /**
   * Level label to display
   */
  levelLabel: string
}

export interface TeamProfileSectionProps {
  /**
   * Profile name (gradient title)
   */
  profileName: string
  /**
   * Description text
   */
  description: string
  /**
   * Personality trait pills
   */
  traits: TraitPill[]
  /**
   * Variant for team or individual
   */
  variant: 'team' | 'individual'
  /**
   * Optional header title
   */
  headerTitle?: string
  /**
   * Optional header icon
   */
  headerIcon?: React.ReactNode
}

/**
 * Gets gradient color for pill indicator based on level
 */
const getPillGradient = (level: 'excellent' | 'good' | 'needsWork'): string => {
  switch (level) {
    case 'excellent':
      return 'linear-gradient(360deg, #4ADE80 0%, #287845 23.08%, #FFFFFF 100%)'
    case 'good':
      return 'linear-gradient(360deg, #FFD196 0%, #FFAB40 23.08%, #FFFFFF 100%)'
    case 'needsWork':
      return 'linear-gradient(360deg, #F48E8E 0%, #D55252 23.08%, #FFFFFF 100%)'
  }
}

/**
 * Gets text color for pill level
 */
const getLevelColor = (level: 'excellent' | 'good' | 'needsWork'): string => {
  switch (level) {
    case 'excellent':
      return '#4ADE80'
    case 'good':
      return '#FFAB40'
    case 'needsWork':
      return '#D55252'
  }
}

/**
 * Gets glow color for pill indicator
 */
const getGlowColor = (level: 'excellent' | 'good' | 'needsWork'): string => {
  switch (level) {
    case 'excellent':
      return '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 2px #287845'
    case 'good':
      return '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(255, 171, 64, 0.4)'
    case 'needsWork':
      return '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(213, 82, 82, 0.4)'
  }
}

/**
 * TeamProfileSection - Displays team/individual profile with traits
 *
 * Features:
 * - Responsive two-column layout (row on desktop, column on mobile)
 * - Left: Profile name and description
 * - Right: Personality trait pills with level indicators
 * - Variant-aware background colors
 * - Gradient glow effects
 */
export const TeamProfileSection: React.FC<TeamProfileSectionProps> = ({
  profileName,
  description,
  traits,
  variant: _variant, // Reserved for future background color variations
  headerTitle,
  headerIcon
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%'
      }}
    >
      <Box
        sx={{
          flex: 1,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          padding: '32px',
          gap: '16px',
          background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(6px)',
          borderRadius: '16px'
        }}
      >
        {/* Header Section */}
        {(headerTitle || headerIcon) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              padding: '0px'
            }}
          >
            {/* Icon */}
            {headerIcon && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0px',
                  width: '48px',
                  height: '48px',
                  background: 'rgba(255, 171, 64, 0.2)',
                  borderRadius: '10px',
                  flex: 'none'
                }}
              >
                {headerIcon}
              </Box>
            )}

            {/* Title */}
            {headerTitle && (
              <Typography
                sx={{
                  ...INTERAX_TYPOGRAPHY.headline3,
                  color: INTERAX_COLORS.white
                }}
              >
                {headerTitle}
              </Typography>
            )}
          </Box>
        )}

        {/* Profile Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'column', lg: 'row' },
            gap: '24px',
            width: '100%'
          }}
        >
          {/* Part 1: Profile Name & Description */}
          <Box
            sx={{
              flex: 1,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '32px',
              gap: '16px',
              background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(6px)',
              borderRadius: '16px'
            }}
          >
            {/* Gradient Profile Name */}
            <Typography
              sx={{
                fontFamily: 'Lexend Deca',
                fontWeight: 700,
                fontSize: '36px',
                lineHeight: '44px',
                background: 'linear-gradient(90deg, #FFD196 0%, #FFAB40 50%, #FFFFFF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textAlign: 'center',
                filter: 'drop-shadow(0px 0px 20px rgba(255, 171, 64, 0.4))'
              }}
            >
              {profileName}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body2,
                color: INTERAX_COLORS.thunderGrey,
                textAlign: 'center',
                maxWidth: '600px'
              }}
            >
              {description}
            </Typography>
          </Box>

          {/* Part 2: Personality Traits Pills */}
          <Box
            sx={{
              flex: 1,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '24px',
              gap: '10px',
              background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(6px)',
              borderRadius: '16px'
            }}
          >
            {/* Inner frame with traits */}
            <Box
              sx={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '24px',
                gap: '24px',
                isolation: 'isolate',
                position: 'relative',
                flex: 1,
                background: `radial-gradient(50.19% 50.19% at 50% 50%, ${INTERAX_COLORS.white20} 0%, rgba(255, 255, 255, 0) 100%), rgba(33, 38, 41, 0.4)`,
                backgroundBlendMode: 'soft-light, normal',
                border: `1px solid ${INTERAX_COLORS.white04}`,
                boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08)',
                borderRadius: '10px',
                overflow: 'hidden'
              }}
            >
              {/* Orange glow ellipse at bottom */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '244px',
                  height: '177px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bottom: '-100px',
                  background: INTERAX_COLORS.sun,
                  filter: 'blur(75px)',
                  zIndex: 2
                }}
              />

              {/* Title */}
              <Typography
                sx={{
                  ...INTERAX_TYPOGRAPHY.headline5,
                  color: INTERAX_COLORS.white,
                  alignSelf: 'stretch',
                  zIndex: 3
                }}
              >
                Personality traits
              </Typography>

              {/* Description */}
              <Typography
                sx={{
                  ...INTERAX_TYPOGRAPHY.body2,
                  color: INTERAX_COLORS.thunderGrey,
                  alignSelf: 'stretch',
                  zIndex: 3
                }}
              >
                An overview of your emotional experience reported during the gaming session.
              </Typography>

              {/* Pills Container */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '16px',
                  flexWrap: 'wrap',
                  alignSelf: 'stretch',
                  zIndex: 4
                }}
              >
                {traits.map((trait, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 12px 12px 18px',
                      gap: '16px',
                      height: '56px',
                      background: 'rgba(33, 38, 41, 0.8)',
                      boxShadow: 'inset 2px 2px 5px rgba(255, 255, 255, 0.25)',
                      borderRadius: '40px',
                      flex: 1,
                      minWidth: '200px'
                    }}
                  >
                    {/* Left: Trait name and level */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        padding: '0px',
                        flex: 1
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: '#FFF2F2'
                        }}
                      >
                        {trait.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: getLevelColor(trait.level)
                        }}
                      >
                        {trait.levelLabel}
                      </Typography>
                    </Box>

                    {/* Right: Glowing circle indicator */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: '32px',
                        height: '32px',
                        flexShrink: 0
                      }}
                    >
                      {/* Outer circle (shadow) */}
                      <Box
                        sx={{
                          position: 'absolute',
                          width: '32px',
                          height: '32px',
                          background: 'rgba(33, 38, 41, 0.4)',
                          boxShadow: 'inset 2px 2px 3px rgba(0, 0, 0, 0.5)',
                          borderRadius: '50%'
                        }}
                      />
                      {/* Inner gradient circle */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '4px',
                          top: '4px',
                          width: '24px',
                          height: '24px',
                          background: getPillGradient(trait.level),
                          border: '1px solid rgba(255, 255, 255, 0.25)',
                          boxShadow: getGlowColor(trait.level),
                          borderRadius: '50%'
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default TeamProfileSection
